import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { node, oneOfType, arrayOf, number, func } from 'prop-types';
import { toParams } from 'utils/history';
import Context from './Context';
import { addSearchParams } from './utils';

export const getScanPlacementIdsKey = (sourceId) =>
  `scan-placement-ids-${sourceId}`;

/**
 * Navigation provider for scan-context placement editing.
 *
 * Instead of fetching its own list via GraphQL (like the standard
 * NavigationProvider), this reads pre-stored placement IDs from
 * localStorage (keyed by sourceId). The IDs match the exact
 * filtered/sorted list the user saw on the scan page.
 *
 * localStorage is used instead of sessionStorage so that
 * "open in new tab" preserves prev/next navigation.
 */
function ScanNavigationProvider({ id, buildUrl, children }) {
  const { search } = useLocation();
  const params = toParams(search);
  const { sourceId } = params;

  const ids = useMemo(() => {
    if (!sourceId) return [];
    try {
      const stored = localStorage.getItem(getScanPlacementIdsKey(sourceId));
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [sourceId]);

  const index = ids.indexOf(id);
  const prevId = index > 0 ? ids[index - 1] : null;
  const nextId = index >= 0 && index < ids.length - 1 ? ids[index + 1] : null;

  const prev = prevId ? buildUrl(prevId) : null;
  const next = nextId ? buildUrl(nextId) : null;

  const prevLink = addSearchParams(prev, null, params);
  const nextLink = addSearchParams(next, null, params);

  const value = useMemo(
    () => ({
      prev: prevLink,
      next: nextLink,
      disabled: false,
      refetch: () => {},
    }),
    [prevLink, nextLink],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

ScanNavigationProvider.propTypes = {
  id: number,
  buildUrl: func.isRequired,
  children: oneOfType([node, arrayOf(node)]).isRequired,
};

ScanNavigationProvider.defaultProps = {
  id: null,
};

export default ScanNavigationProvider;
